import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Button,
  CompassColor,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { InformationalScreen } from "@/components/survey/questionTypes/core";

import { CheckboxButton } from "../../checkbox";

declare module "@/contexts" {
  interface SurveyAnswers {
    liabilityWaiverEatingDisorder?: boolean;
    liabilityWaiverBMI?: boolean;
    liabilityWaiverIdealWeight?: boolean;
    liabilityWaiverPregnant?: boolean;
  }
}

const QUESTION_ID_EATING_DISORDER = "liabilityWaiverEatingDisorder";
const QUESTION_ID_BMI = "liabilityWaiverBMI";
const QUESTION_ID_IDEAL_WEIGHT = "liabilityWaiverIdealWeight";
const QUESTION_ID_PREGNANT = "liabilityWaiverPregnant";

const LiabilityWaiver: (
  questionId: string,
) => React.FC<QuestionComponentProps> = (questionId) => {
  const Component: React.FC<QuestionComponentProps> = ({ onClickNext }) => {
    useTranslation();

    const [didAgree, setDidAgree] = useState(false);

    return (
      <InformationalScreen
        heading={<Trans i18nKey="liabilityWaiver.heading" />}
        questionId={questionId}
        backgroundColor={CompassColor.white}
      >
        <Stack gap={Spacing[5]}>
          <Text>
            <Trans i18nKey="liabilityWaiver.body" />
          </Text>
          <CheckboxButton
            checked={didAgree}
            invertLabelColorsOnChecked={false}
            onChange={() => setDidAgree(!didAgree)}
          >
            <Trans i18nKey="liabilityWaiver.agreeText" />
          </CheckboxButton>
          <Button
            colorScheme="primary"
            onClick={() => onClickNext(true)}
            isDisabled={!didAgree}
            size="xl"
            display="flex"
            margin="auto"
            maxWidth="400px"
          >
            <Trans i18nKey="liabilityWaiver.cta" />
          </Button>
        </Stack>
      </InformationalScreen>
    );
  };

  return Component;
};

export const Q_LIABILITY_WAIVER_EATING_DISORDER: QuestionDefinition = {
  id: QUESTION_ID_EATING_DISORDER,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: { liabilityWaiverAlertEatingDisorder },
  }) => !!liabilityWaiverAlertEatingDisorder,
  component: LiabilityWaiver(QUESTION_ID_EATING_DISORDER),
};

export const Q_LIABILITY_WAIVER_BMI: QuestionDefinition = {
  id: QUESTION_ID_BMI,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({ surveyAnswers: { liabilityWaiverAlertBMI } }) =>
    !!liabilityWaiverAlertBMI,
  component: LiabilityWaiver(QUESTION_ID_BMI),
};

export const Q_LIABILITY_WAIVER_IDEAL_WEIGHT: QuestionDefinition = {
  id: QUESTION_ID_IDEAL_WEIGHT,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: { liabilityWaiverAlertIdealWeight },
  }) => !!liabilityWaiverAlertIdealWeight,
  component: LiabilityWaiver(QUESTION_ID_IDEAL_WEIGHT),
};

export const Q_LIABILITY_WAIVER_PREGNANT: QuestionDefinition = {
  id: QUESTION_ID_PREGNANT,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({ surveyAnswers: { liabilityWaiverAlertPregnant } }) =>
    !!liabilityWaiverAlertPregnant,
  component: LiabilityWaiver(QUESTION_ID_PREGNANT),
};
